import React, { useCallback, useMemo, useState } from 'react';
import { SelectOption, UTMInspection, MUTUTInspectionItem } from '../../../../utils/models';
import { matchPhrases, sortTMLReadings } from '../../../../utils/utilities';

import AutTable from '../../../blocks/aut-grid-table';
import CustomUTMInspectionItemForm from '../../../forms/custom-utm/customUTMInspectionItemForm';
import CustomUTMInspectionItemEditForm from '../../../forms/custom-utm/customUTMInspectionItemEditForm';
import CustomNewItem from '../../custom-general/custom-new-item';
import ExcelImport from '../../../common/excelImport';

import * as data from '../../../../utils/data';
import * as toastService from '../../../../services/toastService';

interface UTMInspectionItemReading extends Pick<MUTUTInspectionItem, "size" | "comments" | "description" | "nominal" | "tmlLocation"> {}

interface CustomUTMInspectionReadingsProps {
    corrosionAllowance: number;
    options: SelectOption[];
    includeSize: boolean;
    inspectionItem: UTMInspection;
    onReadingChange: (readings: UTMInspectionItemReading[]) => void;
}

const CustomUTMInspectionReadings: React.FC<CustomUTMInspectionReadingsProps> = ({ corrosionAllowance, options, onReadingChange, includeSize, inspectionItem,  }) => {
    const [readingToEdit, setReadingToEdit] = useState<UTMInspectionItemReading | null>(null);

    const handleReadingAdd = (newItem: UTMInspectionItemReading) => {
		const readingMatch = inspectionItem.items.find((item: UTMInspectionItemReading) => item.tmlLocation === newItem.tmlLocation);
		if (readingMatch) return toastService.error('Item with the same TML Location exists!');

        onReadingChange([...inspectionItem.items, newItem]);
	};

	const handleReadingDelete = (itemToDelete: UTMInspectionItemReading): void => {
        const updatedItems = inspectionItem.items.filter((item: UTMInspectionItemReading) => item.tmlLocation !== itemToDelete.tmlLocation);
        onReadingChange(updatedItems);
	};

    const handleReadingEdit = (updatedItem: UTMInspectionItemReading) => {
        const otherItemWithSameName = inspectionItem.items
            .filter((item) => item.tmlLocation !== readingToEdit!.tmlLocation)
            .find((item) => matchPhrases(item.tmlLocation.toString(), updatedItem.tmlLocation.toString()))

		if (otherItemWithSameName) {
            return toastService.error('Another items already exists with that name! ' + otherItemWithSameName.tmlLocation);
        }

        const items = inspectionItem.items.map((item) => item.tmlLocation === readingToEdit!.tmlLocation ? updatedItem : item);
        onReadingChange(items);
        setReadingToEdit(null);
    };

    const utmReadingColumns = useMemo(() => {
        return data.getUTMReadingColumns(inspectionItem.startColumn, inspectionItem.endColumn);
    }, [inspectionItem.startColumn, inspectionItem.endColumn]);

    const generateDataColumns = useCallback((items: any[]) => {
        return data.getCustomUTMTableColumns(inspectionItem.startColumn, inspectionItem.endColumn, includeSize, items, corrosionAllowance);
    }, [inspectionItem, includeSize, corrosionAllowance]);

    const excelTemplate = useMemo(() => {
        const compressedReadings = utmReadingColumns.reduce((accumulator: any, currentValue) => {
            accumulator[currentValue] = 0;

            return accumulator;
        }, {});

        const dataShape = {
            tmlLocation: '',
			description: '',
			nominal: '',
            ...compressedReadings,
			comments: '',
        };

        return [dataShape];

    }, [utmReadingColumns]);

	return ( 
        <>
            {readingToEdit ? (
                <CustomUTMInspectionItemEditForm
                    options={options} 
                    inspectedItem={readingToEdit}
                    onCancelEdit={() => setReadingToEdit(null)}
                    onEdit={handleReadingEdit}
                />
            ) : (
                <CustomNewItem label='Add New UTM Item'>
                    <CustomUTMInspectionItemForm
                        includeSize={includeSize}
                        options={options} 
                        defaultColumns={utmReadingColumns} 
                        onAdd={handleReadingAdd} 
                    />
                </CustomNewItem>
            )}


            <ExcelImport 
                template={excelTemplate}
                generateTemplateColumns={generateDataColumns} 
                onImportData={(data) => onReadingChange(data)}
            />

            <div className="divider my-4"></div>

            <AutTable
                tableColumns={generateDataColumns(inspectionItem.items)}
                data={sortTMLReadings(inspectionItem.items)}
                onDeleteItem={handleReadingDelete}
                onEditItem={(reading: UTMInspectionItemReading) => setReadingToEdit(reading)}
            />
        </>
     );
}
 
export default CustomUTMInspectionReadings;
